import { jsx as _jsx } from "react/jsx-runtime";
// One live shell session: an xterm.js terminal wired to the gateway's terminal WebSocket.
// Terminal bytes travel as binary frames; JSON text frames carry control (ready, prompt,
// pasted, error, exit). Pasted secrets are typed by the gateway — never by this browser.
import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
export const TerminalTab = forwardRef(function TerminalTab({ url, token, host, account, visible, onEvent, onPrompt }, ref) {
    const boxRef = useRef(null);
    const wsRef = useRef(null);
    const termRef = useRef(null);
    const fitRef = useRef(null);
    const cbRef = useRef({ onEvent, onPrompt });
    cbRef.current = { onEvent, onPrompt };
    useImperativeHandle(ref, () => ({
        paste(credentialId) {
            wsRef.current?.send(JSON.stringify({ t: 'paste', credentialId }));
            termRef.current?.focus();
        },
        reauth(t) {
            wsRef.current?.send(JSON.stringify({ t: 'reauth', token: t }));
        },
        focus() {
            termRef.current?.focus();
        },
        fit() {
            fitRef.current?.fit();
        },
    }));
    useEffect(() => {
        // xterm.js is loaded on demand so the rest of the admin UI (and its tests) never pays for it.
        let disposed = false;
        let teardown = null;
        void Promise.all([import('@xterm/xterm'), import('@xterm/addon-fit')]).then(([xterm, fitMod]) => {
            if (disposed)
                return;
            const term = new xterm.Terminal({
                cursorBlink: true,
                fontFamily: "'JetBrains Mono', ui-monospace, Menlo, Consolas, monospace",
                fontSize: 13,
                scrollback: 5000,
                theme: { background: '#101614', foreground: '#d6e2dc', cursor: '#6fd3a8', selectionBackground: '#2f6b55' },
            });
            const fit = new fitMod.FitAddon();
            term.loadAddon(fit);
            termRef.current = term;
            fitRef.current = fit;
            if (boxRef.current)
                term.open(boxRef.current);
            try {
                fit.fit();
            }
            catch { /* not visible yet */ }
            const ws = new WebSocket(url);
            ws.binaryType = 'arraybuffer';
            wsRef.current = ws;
            const enc = new TextEncoder();
            ws.onopen = () => {
                ws.send(JSON.stringify({ t: 'auth', token, host, account, cols: term.cols, rows: term.rows }));
            };
            ws.onmessage = (ev) => {
                if (ev.data instanceof ArrayBuffer) {
                    term.write(new Uint8Array(ev.data));
                    return;
                }
                let msg;
                try {
                    msg = JSON.parse(String(ev.data));
                }
                catch {
                    return;
                }
                if (msg.t === 'prompt')
                    cbRef.current.onPrompt({ kind: msg.kind ?? null, account: msg.account ?? null });
                if (msg.t === 'exit')
                    term.write(`\r\n\x1b[90m[session ended: ${String(msg.reason ?? 'closed')}]\x1b[0m\r\n`);
                if (msg.t === 'error' && !msg.recoverable)
                    term.write(`\r\n\x1b[31m${String(msg.message ?? 'error')}\x1b[0m\r\n`);
                cbRef.current.onEvent(msg);
            };
            ws.onclose = () => cbRef.current.onEvent({ t: 'closed' });
            const dataSub = term.onData((d) => {
                if (ws.readyState === WebSocket.OPEN)
                    ws.send(enc.encode(d));
            });
            const resizeSub = term.onResize(({ cols, rows }) => {
                if (ws.readyState === WebSocket.OPEN)
                    ws.send(JSON.stringify({ t: 'resize', cols, rows }));
            });
            const onWinResize = () => { try {
                fit.fit();
            }
            catch { /* hidden */ } };
            window.addEventListener('resize', onWinResize);
            teardown = () => {
                window.removeEventListener('resize', onWinResize);
                dataSub.dispose();
                resizeSub.dispose();
                ws.close();
                term.dispose();
            };
        }).catch((e) => cbRef.current.onEvent({ t: 'error', code: 'LOAD_FAILED', message: `Could not load the terminal: ${String(e)}` }));
        return () => {
            disposed = true;
            teardown?.();
        };
        // A session is opened once; url/token/host changes open a new tab instead.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (visible) {
            try {
                fitRef.current?.fit();
            }
            catch { /* ignore */ }
            termRef.current?.focus();
        }
    }, [visible]);
    return _jsx("div", { ref: boxRef, style: { display: visible ? 'block' : 'none', height: '100%', minHeight: 420, padding: 8, background: '#101614' } });
});
//# sourceMappingURL=TerminalTab.js.map